import {
  HttpErrorResponse,
  HttpEvent,
  HttpEventType,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import {
  NotificationCondition,
  ON_POST_PUT_URLS_TOAST_CONDITIONS,
  matchesToastRoute,
} from '@shared/constants/notification-conditions';
import { REQUEST_TOAST_CONTEXT_TOKEN } from '@shared/types/api-request-context';
import { CommonErrorData } from '@shared/types/error-responses';
import { NotificationsService } from 'app/notifications/notifications.service';
import { ToastData, ToastType } from 'app/notifications/notifications.types';

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  constructor(private notificationsService: NotificationsService) {}

  public intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    const toastContext = request.context.get(REQUEST_TOAST_CONTEXT_TOKEN);
    const { canShow, canShowProgress } = toastContext?.silent
      ? { canShow: false, canShowProgress: false }
      : this.canShowToast(request);
    let toastData: ToastData;

    if (canShowProgress) {
      toastData = this.notificationsService.openToast(ToastType.Progress, {
        title: toastContext?.loading?.title || 'Sending data...',
        description: toastContext?.loading?.description,
        action: toastContext?.loading?.action,
        options: {
          disableTimeOut: true,
          isExpandable: toastContext?.loading?.isExpandable,
        },
      });
    }

    return next.handle(request).pipe(
      tap((event) => {
        if (canShow && event.type === HttpEventType.Response) {
          if (toastData) {
            this.notificationsService.removeToast(toastData.id);
          }

          this.notificationsService.openToast(ToastType.Success, {
            title: toastContext?.success?.title || 'Data sent and saved successfully',
            description: toastContext?.success?.description,
            action: toastContext?.success?.action,
            options: {
              isExpandable: toastContext?.success?.isExpandable,
            },
          });
        }
      }),
      catchError((e: HttpErrorResponse) => {
        const error = e.error as CommonErrorData;

        if (error && toastContext?.silentErrorCodes?.includes(error.code)) {
          return throwError(() => e);
        }

        console.error('HttpErrorInterceptor:', error || e);

        const errorContext = toastContext?.error ? toastContext.error(e?.status) : null;

        if (e) {
          switch (e.status) {
            case 401:
            case 403: {
              // Show current error notification
              if (canShow) {
                if (toastData) {
                  this.notificationsService.removeToast(toastData.id);
                }

                this.notificationsService.openToast(ToastType.Error, {
                  title: errorContext?.title || 'Access is denied',
                  description: errorContext?.description || error?.description,
                  action: errorContext?.action,
                  options: {
                    isExpandable: errorContext?.isExpandable,
                  },
                });
              }
              break;
            }

            case 404: {
              // Show current error notification
              if (canShow) {
                if (toastData) {
                  this.notificationsService.removeToast(toastData.id);
                }

                this.notificationsService.openToast(ToastType.Error, {
                  title: errorContext?.title || 'Not found',
                  description: errorContext?.description || error?.description,
                  action: errorContext?.action,
                  options: {
                    isExpandable: errorContext?.isExpandable,
                  },
                });
              }
              break;
            }

            default: {
              // Show current error notification
              if (canShow) {
                if (toastData) {
                  this.notificationsService.removeToast(toastData.id);
                }

                this.notificationsService.openToast(ToastType.Error, {
                  title: errorContext?.title || 'Something went wrong',
                  description: errorContext?.description || error?.description,
                  action: errorContext?.action,
                  options: {
                    isExpandable: errorContext?.isExpandable,
                  },
                });
              }
              break;
            }
          }
        } else {
          // Show current error notification
          if (canShow) {
            if (toastData) {
              this.notificationsService.removeToast(toastData.id);
            }

            this.notificationsService.openToast(ToastType.Error, {
              title: errorContext?.title || 'Something went wrong',
              description: errorContext?.description || error?.description,
              action: errorContext?.action,
              options: {
                isExpandable: errorContext?.isExpandable,
              },
            });
          }
        }

        return throwError(() => e);
      }),
      finalize(() => {
        if (toastData?.id) {
          this.notificationsService.removeToast(toastData.id);
        }
      }),
    );
  }

  public canShowToast(request: HttpRequest<unknown>): NotificationCondition {
    // filter by method and url
    if (request.method === 'POST' || request.method === 'PUT' || request.method === 'DELETE') {
      for (const [url, condition] of Object.entries(ON_POST_PUT_URLS_TOAST_CONDITIONS)) {
        if (matchesToastRoute(request.url, url)) {
          return condition;
        }
      }

      return { canShow: true, canShowProgress: true };
    } else {
      return { canShow: false, canShowProgress: false };
    }
  }
}
